/**
 * Integrytas Group - Base App (MOBILE)
 * Page: UNE.PREFERENCES — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/UnePreferenceCenterPage.tsx. A
 * DISTINCT presentation (workspace scope selector + dense card list + bottom-sheet
 * channel picker) over the SAME data/logic layer only:
 *   - reads via apiDb (une_event_category + une_mandatory_rule + une_user_preference),
 *     scoped to the active workspace exactly like the desktop matrix,
 *   - every preference write routes through the Common Routine (routedUpdate /
 *     routedInsert) — §2 single write path; never a direct apiDb write. The
 *     (user, category, workspace) unique means we resolve the existing surrogate
 *     PK first, then route ADD vs CHANGE, mirroring the desktop page.
 * Channels an administrator marked Mandatory are locked on (conflicting options
 * disabled), same rule as the desktop. It never re-renders the desktop grid; §16.
 *
 * une_event_category carries a plain `name` column (no _tl translation table),
 * so — like the desktop source of truth — the category name is read directly;
 * the four delivery choices are a CHECK-enum whose labels go through i18n (§17.B11).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Bell, BellOff, Lock, X } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { useActiveWorkspace } from '../../lib/use_workspace';
import { MobilePageHeader, MobileContentCard, MobileCardRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

// The four delivery choices are a CHECK-enum on une_user_preference.channel_pref;
// labels are localised via t() (§17.B11), never the stored literal.
type Pref = 'INAPP_ONLY' | 'EMAIL_ONLY' | 'BOTH' | 'NONE';

interface PrefOption {
  value: Pref;
  labelKey: string;
  fallback: string;
  inapp: boolean;
  email: boolean;
}

const PREF_OPTIONS: PrefOption[] = [
  { value: 'BOTH', labelKey: 'mobile.une.pref.BOTH', fallback: 'Both', inapp: true, email: true },
  { value: 'INAPP_ONLY', labelKey: 'mobile.une.pref.INAPP_ONLY', fallback: 'In-App only', inapp: true, email: false },
  { value: 'EMAIL_ONLY', labelKey: 'mobile.une.pref.EMAIL_ONLY', fallback: 'Email only', inapp: false, email: true },
  { value: 'NONE', labelKey: 'mobile.une.pref.NONE', fallback: 'Muted', inapp: false, email: false },
];

const DEFAULT_PREF: Pref = 'BOTH';

interface Category {
  une_event_category_id: number;
  internal_code: string;
  name: string;
}
interface Mandatory {
  une_event_category_id: number;
  channel: 'INAPP' | 'EMAIL';
}

type PrefFacet = 'all' | 'notifying' | 'muted' | 'locked';

interface RawCategory {
  une_event_category_id: number;
  internal_code: string;
  name: string;
}
interface RawMandatory {
  une_event_category_id: number;
  channel: 'INAPP' | 'EMAIL';
  une_workspace_id: number | null;
}
interface RawPreference {
  une_event_category_id: number;
  channel_pref: Pref;
  une_workspace_id: number | null;
}

/* rbac-affordance-ok: self-service. Every write here is the signed-in
   user's OWN une_user_preference row (user_id = session.effective_user.user_id),
   not page-scoped data, so csm_role_page_access.can_change is the wrong gate —
   applying it would stop a user setting their own notification preferences. */
export function MobileUnePreferenceCenterPage({ session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  const userId = session.effective_user.user_id;
  const { workspaces, activeCode, activeId, setActive } = useActiveWorkspace();

  const [categories, setCategories] = useState<Category[]>([]);
  const [mandatory, setMandatory] = useState<Mandatory[]>([]);
  const [prefs, setPrefs] = useState<Record<number, Pref>>({});
  const [filter, setFilter] = useState('');
  const [facet, setFacet] = useState<PrefFacet>('all');
  const [selected, setSelected] = useState<Category | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const prefLabel = useCallback(
    (value: Pref): string => {
      const opt = PREF_OPTIONS.find((o) => o.value === value);
      return opt ? t(opt.labelKey, opt.fallback) : value;
    },
    [t],
  );

  const reload = useCallback(async () => {
    if (activeId == null) return;
    setBusy(true);
    setError(null);
    try {
      const [c, m, p] = await Promise.all([
        apiDb
          .from('une_event_category')
          .select('une_event_category_id, internal_code, name')
          .eq('is_active', 'Y')
          .order('sort_order'),
        apiDb
          .from('une_mandatory_rule')
          .select('une_event_category_id, channel, une_workspace_id')
          .eq('is_active', 'Y'),
        apiDb
          .from('une_user_preference')
          .select('une_event_category_id, channel_pref, une_workspace_id')
          .eq('user_id', userId),
      ]);
      if (c.error) throw c.error;
      if (m.error) throw m.error;
      if (p.error) throw p.error;

      setCategories(((c.data ?? []) as RawCategory[]).map((r) => ({
        une_event_category_id: r.une_event_category_id,
        internal_code: r.internal_code,
        name: r.name,
      })));

      // A mandatory rule with a NULL workspace applies to every workspace.
      setMandatory(
        ((m.data ?? []) as RawMandatory[])
          .filter((r) => r.une_workspace_id == null || r.une_workspace_id === activeId)
          .map((r) => ({ une_event_category_id: r.une_event_category_id, channel: r.channel })),
      );

      const pm: Record<number, Pref> = {};
      ((p.data ?? []) as RawPreference[])
        .filter((r) => r.une_workspace_id === activeId)
        .forEach((r) => { pm[r.une_event_category_id] = r.channel_pref; });
      setPrefs(pm);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [userId, activeId]);

  useEffect(() => { void reload(); }, [reload]);

  const forced = useCallback(
    (catId: number) => ({
      inapp: mandatory.some((mr) => mr.une_event_category_id === catId && mr.channel === 'INAPP'),
      email: mandatory.some((mr) => mr.une_event_category_id === catId && mr.channel === 'EMAIL'),
    }),
    [mandatory],
  );

  // An option is selectable only if it keeps every admin-forced channel on.
  const allowed = useCallback(
    (catId: number, opt: PrefOption): boolean => {
      const f = forced(catId);
      if (f.inapp && !opt.inapp) return false;
      if (f.email && !opt.email) return false;
      return true;
    },
    [forced],
  );

  const currentPref = useCallback(
    (catId: number): Pref => prefs[catId] ?? DEFAULT_PREF,
    [prefs],
  );

  const isLocked = useCallback(
    (catId: number): boolean => {
      const f = forced(catId);
      return f.inapp || f.email;
    },
    [forced],
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return categories.filter((c) => {
      const pref = prefs[c.une_event_category_id] ?? DEFAULT_PREF;
      if (facet === 'notifying' && pref === 'NONE') return false;
      if (facet === 'muted' && pref !== 'NONE') return false;
      if (facet === 'locked' && !isLocked(c.une_event_category_id)) return false;
      if (!q) return true;
      return c.name.toLowerCase().includes(q) || c.internal_code.toLowerCase().includes(q);
    });
  }, [categories, prefs, filter, facet, isLocked]);

  const facets: { key: PrefFacet; label: string }[] = [
    { key: 'all', label: t('mobile.une.facet.all', 'All') },
    { key: 'notifying', label: t('mobile.une.facet.notifying', 'Notifying') },
    { key: 'muted', label: t('mobile.une.facet.muted', 'Muted') },
    { key: 'locked', label: t('mobile.une.facet.locked', 'Locked') },
  ];

  const lockedLabel = (catId: number): string => {
    const f = forced(catId);
    const channels = [
      f.inapp && t('mobile.une.channel.inapp', 'In-App'),
      f.email && t('mobile.une.channel.email', 'Email'),
    ].filter(Boolean) as string[];
    return channels.join(' + ');
  };

  const savePref = async (catId: number, value: Pref) => {
    if (activeId == null) return;
    setActionBusy(true);
    setError(null);
    const previous = prefs[catId];
    // Optimistic: reflect the choice immediately, roll back on failure.
    setPrefs((p) => ({ ...p, [catId]: value }));
    try {
      // §2: the unique is keyed on (user, category, workspace) — not the PK — so
      // resolve the existing row's surrogate PK first, then route ADD vs CHANGE.
      const { data: existing, error: eFind } = await apiDb
        .from('une_user_preference')
        .select('une_user_preference_id')
        .eq('user_id', userId)
        .eq('une_event_category_id', catId)
        .eq('une_workspace_id', activeId)
        .maybeSingle();
      if (eFind) throw eFind;
      const existingId = (existing as { une_user_preference_id: number } | null)?.une_user_preference_id ?? null;
      const { error: e } = existingId != null
        ? await routedUpdate(session, 'une_user_preference', 'une_user_preference_id', existingId, { channel_pref: value })
        : await routedInsert(session, 'une_user_preference', {
            user_id: userId,
            une_workspace_id: activeId,
            une_event_category_id: catId,
            channel_pref: value,
          });
      if (e) throw e;
      setSelected(null);
    } catch (e) {
      setPrefs((p) => {
        if (previous === undefined) {
          const { [catId]: _removed, ...next } = p;
          return next;
        }
        return { ...p, [catId]: previous };
      });
      setError((e as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  const sheetPref = selected ? currentPref(selected.une_event_category_id) : DEFAULT_PREF;

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.une.pref_title', 'Notifications')}
        description={t('mobile.une.pref_description', 'Choose how each category reaches you in this workspace; mandatory channels stay on.')}
      />

      {/* Workspace scope — preferences are set per workspace (§7 multi-tenancy). */}
      <div className="m-fieldcard">
        <div className="m-formrow">
          <label htmlFor="m-une-workspace">{t('mobile.une.workspace', 'Workspace')}</label>
          <select
            id="m-une-workspace"
            className="m-sel"
            value={activeCode}
            onChange={(e) => setActive(e.target.value)}
          >
            {workspaces.map((w) => (
              <option key={w.une_workspace_id} value={w.internal_code}>{w.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.une.search', 'Search categories')}
          aria-label={t('mobile.une.search', 'Search categories')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => facet === v}
        onSelect={(v) => setFacet(v as PrefFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.une.none', 'No categories match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((c) => {
            const pref = currentPref(c.une_event_category_id);
            const muted = pref === 'NONE';
            const locked = isLocked(c.une_event_category_id);
            return (
              <MobileCardRow
                key={c.une_event_category_id}
                lead={muted ? <BellOff /> : <Bell />}
                leadTone={locked ? 'amber' : muted ? 'slate' : 'blue'}
                title={c.name}
                subtitle={
                  c.internal_code +
                  (locked ? ' · ' + t('mobile.une.mandatory', 'Mandatory') + ': ' + lockedLabel(c.une_event_category_id) : '')
                }
                trailing={<MobileStatusPill tone={muted ? 'no' : 'ok'}>{prefLabel(pref)}</MobileStatusPill>}
                chevron
                onClick={() => setSelected(c)}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Channel-picker bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.une.detail', 'Notification preference')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av">{sheetPref === 'NONE' ? <BellOff size={20} /> : <Bell size={20} />}</span>
              <span>
                <div className="m-nm">{selected.name}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              {isLocked(selected.une_event_category_id) && (
                <div className="m-field">
                  <span className="m-k"><Lock size={13} /> {t('mobile.une.mandatory', 'Mandatory')}</span>
                  <span className="m-v">
                    <span className="m-pill m-p-wait">{lockedLabel(selected.une_event_category_id)}</span>
                  </span>
                </div>
              )}
              <div className="m-formrow">
                <label>{t('mobile.une.delivery', 'Delivery')}</label>
                <div className="m-chips">
                  {PREF_OPTIONS.map((opt) => {
                    const ok = allowed(selected.une_event_category_id, opt);
                    const active = sheetPref === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={'m-chip' + (active ? ' m-active' : '')}
                        disabled={!ok || actionBusy}
                        aria-pressed={active}
                        onClick={() => { if (!active) void savePref(selected.une_event_category_id, opt.value); }}
                      >
                        {t(opt.labelKey, opt.fallback)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <p className="m-empty" style={{ textAlign: 'left', padding: '0 4px' }}>
              {t('mobile.une.hint', 'In-App covers both toasts and your inbox. Switch workspace above to set different preferences.')}
            </p>

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
